import { AlertTriangle, BellRing, Sparkles, LucideIcon } from "lucide-react";
import { ComunicadoSistemaEstado, ComunicadoSistemaNivel } from "@/types/comunicado-sistema.type";

interface NivelConfig {
  label: string;
  peso: number;
  Icon: LucideIcon;
  /** Barra/card (fundo + borda + texto). */
  surface: string;
  /** Selo do nível (fundo sólido + texto claro). */
  badge: string;
  /** Badge suave para tabelas (fundo claro + texto + borda). */
  soft: string;
  /** Cor do texto/ícone de destaque. */
  accentText: string;
  /** Bolinha (sino / badge). */
  dot: string;
  /** Borda-esquerda (sino/mural). */
  borderLeft: string;
}

export const NIVEL_CONFIG: Record<ComunicadoSistemaNivel, NivelConfig> = {
  CRITICO: {
    label: "Crítico",
    peso: 3,
    Icon: AlertTriangle,
    surface:
      "bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900 text-red-800 dark:text-red-300",
    badge: "bg-red-600 text-white",
    soft: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900",
    accentText: "text-red-700 dark:text-red-300",
    dot: "bg-red-500",
    borderLeft: "border-l-red-500",
  },
  ALERTA: {
    label: "Alerta",
    peso: 2,
    Icon: BellRing,
    surface:
      "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300",
    badge: "bg-amber-500 text-white",
    soft: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
    accentText: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500",
    borderLeft: "border-l-amber-500",
  },
  INFORMATIVO: {
    label: "Informativo",
    peso: 1,
    Icon: Sparkles,
    surface:
      "bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900 text-blue-800 dark:text-blue-300",
    badge: "bg-blue-600 text-white",
    soft: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
    accentText: "text-blue-700 dark:text-blue-300",
    dot: "bg-blue-500",
    borderLeft: "border-l-blue-500",
  },
};

/**
 * O que cada nível *faz* — fonte única da regra de §2.5, usada nos cartões de
 * escolha do formulário e na prévia. Nível não é só cor: decide o custo de sair
 * da barra e se há aceite.
 */
export const NIVEL_COMPORTAMENTO: Record<
  ComunicadoSistemaNivel,
  { quando: string; barra: string; aceite: string }
> = {
  INFORMATIVO: {
    quando: "Novidades e avisos leves.",
    barra: 'Aparece na barra do topo, com "x" para dispensar.',
    aceite: "Sem confirmação de leitura.",
  },
  ALERTA: {
    quando: "Mudanças que exigem atenção.",
    barra: 'Aparece na barra do topo, com "x" para dispensar.',
    aceite: "Sem confirmação de leitura.",
  },
  CRITICO: {
    quando: "Urgente — manutenção, incidente, prazo.",
    barra: 'Fixa na barra: não tem "x", só sai pelo aceite ou pelo fim da vigência.',
    aceite: 'Exige "Li e aceito" e registra quem confirmou (e quem não).',
  },
};

/** Cores do badge de estado na listagem admin (StatusBadge). */
export const ESTADO_COLOR: Record<ComunicadoSistemaEstado, string> = {
  VIGENTE:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  AGENDADO:
    "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900",
  RASCUNHO: "bg-muted text-muted-foreground border-border",
  EXPIRADO: "bg-muted text-muted-foreground border-border",
  ARQUIVADO: "bg-muted text-muted-foreground border-border",
};

export const ESTADO_LABEL: Record<ComunicadoSistemaEstado, string> = {
  RASCUNHO: "Rascunho",
  AGENDADO: "Agendado",
  VIGENTE: "Vigente",
  EXPIRADO: "Expirado",
  ARQUIVADO: "Arquivado",
};

export const NIVEL_OPTIONS: ComunicadoSistemaNivel[] = ["INFORMATIVO", "ALERTA", "CRITICO"];
